import * as layoutStyles from '../../styles/layout/layout.module.scss';

import Footer from './Footer';
import Header from './desktop/Header';
import Menu from './smartphone/Menu';
import PropTypes from 'prop-types';
import React from 'react';
import ScrollToTop from 'react-scroll-to-top';
import SiteNavigationHtml from './desktop/SiteNavigationHtml';
import TransversalNavigationHtml from './desktop/TransversalNavigationHtml';
import clsx from 'clsx';
import {supportedLanguages} from '../../services/i18n';
import {useTranslation, getI18n} from 'react-i18next';
import { mockLayout } from '../../utils/mockData';
import { graphql } from 'gatsby';
import { getLayoutInformations } from '../../utils/parsing';
import * as theme from '../../styles/theme-scpo.module.scss';

const Layout = React.memo(function Layout (props) {
  const {children, data, sliceContext } = props;
  const {
    locale,
    localizations,
    name,
    logo,
    logoPartner,
    menu,
    corporateMenu,
    communityMenu,
    footer,
    siteURL = sliceContext.websiteRoot,
  } = (props.mock || sliceContext.mock)
    ? mockLayout : (data) ?
      getLayoutInformations(data, sliceContext.websiteRoot) : props;

  //setup i18n
  if (sliceContext && sliceContext.locale !== null && getI18n().language !== sliceContext.locale) {
    getI18n().changeLanguage(sliceContext.locale);
  }

  const {t} = useTranslation();
  const [displayNavigationLinks, setDisplayNavigationLinks] = React.useState(false);
  React.useEffect(() => {
    // remove tabIndex -1 for accessibility
    const gatsbyFocusWrapper = document.getElementById('gatsby-focus-wrapper');
    if (gatsbyFocusWrapper) {
      gatsbyFocusWrapper.removeAttribute('tabIndex');
    }
  }, []);
  return (
    <>
      <nav
        aria-label={t('accessibility.nav.label')}
        className={theme.themeScpo}
      >
        <div
          className={displayNavigationLinks ?
            layoutStyles.accessibilityLinks
            : clsx(layoutStyles.accessibilityLinksHide, layoutStyles.accessibilityLinks)}
        >
          <ul>
            <li>
              <a
                href="#content"
                onFocus={() => setDisplayNavigationLinks(true)}
              >
                {t('skip.to.content')}
              </a>{' '}
                /
            </li>
            <li>
              <a href="#surHeader">{t('skip.to.sur.header')}</a> /
            </li>
            <li>
              <a
                href="#navigation"
                onFocus={() => setDisplayNavigationLinks(true)}
              >
                {t('skip.to.main.navigation')}
              </a>{' '}
                /
            </li>
            <li>
              <a
                href="#searchForm"
                onBlur={() => setDisplayNavigationLinks(false)}
              >
                {t('skip.to.search.form')}
              </a>{' '}
                /
            </li>
            <li>
              <a
                href="#footer"
                onBlur={() => setDisplayNavigationLinks(false)}
                onFocus={() => setDisplayNavigationLinks(true)}
              >
                {t('skip.to.footer')}
              </a>
            </li>
          </ul>
        </div>
      </nav>
      <div
        className={clsx(layoutStyles.rootNav, theme.themeScpo)}
      >
        <nav
          aria-label={t('transversalNav.label')}
          className={layoutStyles.desktop}
          id="surHeader"
        >
          <TransversalNavigationHtml
            corporateMenu={corporateMenu}
            locale={locale}
            localizations={localizations}
            themeClass={theme.themeScpo}
            websiteRoot={siteURL}
          />
        </nav>
        <Header
          communityMenu={communityMenu}
          locale={locale}
          localizations={localizations}
          logo={logo}
          logoPartner={logoPartner}
          menu={menu}
          name={name}
          styleClass={layoutStyles.desktop}
        />
        <nav
          className={clsx(
            layoutStyles.desktop,
            layoutStyles.layoutContainer,
            layoutStyles.headerContainer
          )}
        >
          <SiteNavigationHtml
            item
            locale={locale}
            localizations={localizations}
            menu={menu}
            siteURL={siteURL}
          />
        </nav>
        <nav className={layoutStyles.smartphone}>
          <Menu
            communityMenu={communityMenu}
            corporateMenu={corporateMenu}
            currentLocale={getI18n().language}
            languages={supportedLanguages}
            locale={locale}
            localizations={localizations}
            logo={logo}
            logoPartner={logoPartner}
            menu={menu}
            name={name}
            siteURL={siteURL}
          />
        </nav>
        <main id="content"
          style={{width: '100%'}}
        >
          {children}
          {
            <ScrollToTop
              aria-label={t('scrollToTop.label')}
              className={layoutStyles.scrollToTopClass}
              color={'white'}
              smooth
            />
          }
        </main>
        <div
          id="footer"
          role="contentinfo"
        >
          <div
            className={clsx(layoutStyles.footer)}
          >
            <Footer
              footer={footer}
              locale={locale}
            />
          </div>
        </div>
      </div>
    </>
  );
});

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  data: PropTypes.object,
  mock: PropTypes.bool,
  siteURL: PropTypes.string,
  sliceContext: PropTypes.shape({
    locale: PropTypes.string,
    websiteRoot: PropTypes.string,
    mock: PropTypes.bool,
  }),
};

export const query = graphql`
  query ($websiteId: String!) {
    website: cmsPortalWebsite(id: { eq: $websiteId }) {
      ...WebsiteFragment
    }
    frGeneralSettings: cmsPortalGeneralSettings(locale: { eq: "fr" }) {
      ...GeneralSettingsFragment
    }
    enGeneralSettings: cmsPortalGeneralSettings(locale: { eq: "en" }) {
      ...GeneralSettingsFragment
    }
  }
`;

export default Layout;